import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { Modal, Flex, Text, Paragraph, Button } from '@nula/core-ui';
import { useCancelSubscription } from './use-subscription';

interface Props {
  open: boolean;
  planName: string;
  onClose: () => void;
  onCanceled: (effectiveAt: string | null) => void;
  /** Scope the cancel to a specific workspace (defaults to the active one). */
  workspaceId?: string;
  /**
   * A canceled TEAM workspace is made read-only by `ensure_workspace_writable`
   * — members get 402s on create/rename/archive/delete/invite. Only a personal
   * workspace actually falls back to a usable Basic plan.
   */
  workspaceType?: 'personal' | 'team';
}

export function CancelRetentionModal({
  open,
  planName,
  onClose,
  onCanceled,
  workspaceId,
  workspaceType = 'personal',
}: Props) {
  const cancel = useCancelSubscription(workspaceId);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = async () => {
    setError(null);
    try {
      const res = await cancel.mutateAsync();
      onCanceled(res.effectiveAt);
    } catch (err) {
      Sentry.captureException(err);
      setError("Couldn't cancel right now. Please try again.");
    }
  };

  const footer = (
    <>
      <Button variant="text" loading={cancel.isPending} onClick={handleCancel}>
        Cancel plan
      </Button>
      <Button variant="primary" pressEffect="glow" onClick={onClose}>
        Keep {planName}
      </Button>
    </>
  );

  return (
    <Modal open={open} onClose={onClose} title={`Stay on ${planName}?`} footer={footer} centered>
      <Flex vertical gap="sm">
        {workspaceType === 'team' ? (
          <Paragraph>
            Cancelling never deletes anything — your documents, canvases, and recordings all stay.
            But once the plan ends this workspace becomes read-only for every member: nobody can
            create, rename, archive, or delete anything, or invite people. Resubscribing restores
            full access.
          </Paragraph>
        ) : (
          <Paragraph>
            Your work is always safe. Cancelling never deletes anything. Your documents, canvases,
            and recordings all stay, and you can keep using them on the free Basic plan.
          </Paragraph>
        )}
        <Paragraph>
          You&apos;ll keep {planName} until the end of your current billing period, so there&apos;s
          no rush. You can come back anytime.
        </Paragraph>
        {error && <Text type="danger">{error}</Text>}
      </Flex>
    </Modal>
  );
}
